/* eslint-disable jira/filename-case */

import type { RenderPolicy } from 'relay-runtime';
import { fg } from '@atlassian/jira-feature-gating';
import {
	createRelayEnvironment,
	type AtlassianRelayEnvironment,
} from '@atlassian/relay-environment/createRelayEnvironment';
import { getNetworkOptions } from '@jira/platform__graphql-relay-fetch/src/getNetworkOptions.tsx';
import relayLogHandler, {
	requiredFieldLogger as jiraRequiredFieldLogger,
} from '@jira/platform__graphql-relay-log/src/index.tsx';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import { getConnectionEventReporter } from './getConnectionEventReporter.tsx';

/**
 * Relay's default `partial` render policy lets a read that is missing data render anyway, with the
 * absent fields resolving to `null`. Under streaming SSR that turns a query which has not arrived by
 * the SSR deadline into a fully laid-out page of empty fields rather than a skeleton, because the
 * store usually holds enough from other queries to satisfy the shallow root selections. `full` makes
 * Relay suspend or fail those reads instead, so the nearest Suspense boundary renders its fallback.
 */
const getDefaultRenderPolicy = (): RenderPolicy => {
	if (__SERVER__ && fg('jfp-magma-ssr-relay-full-render-policy')) {
		return 'full';
	}
	return 'partial';
};

const platformRelayEnvironment: AtlassianRelayEnvironment = createRelayEnvironment({
	log: relayLogHandler,
	requiredFieldLogger: (event) => {
		if (fg('fun-2278_logging')) {
			jiraRequiredFieldLogger(event);
		}
	},
	networkOptions: getNetworkOptions(),
	getDefaultRenderPolicy,
	getEnableUfoHolds: () => fg('platform_ufo_relay_operation_holds'),
	wsClientOptions: {
		getConnectionEventReporter,
	},
});

autoBeforeEachTest(() => {
	// @ts-expect-error - Relay's source type does not expose clear()
	platformRelayEnvironment.getStore().getSource().clear();
});

function getPlatformRelayEnvironment(): AtlassianRelayEnvironment {
	return platformRelayEnvironment;
}

export default getPlatformRelayEnvironment;
